import { useState, useCallback } from 'react';
import authService from '../../../services/authService';

/**
 * Custom hook for managing PDF generation functionality
 */
export const usePdfGeneration = (pentestServer, refreshTableData, fetchResultFiles, currentScanId, isPentestLoading, showConfirm = null) => {
  const [generatingPdf, setGeneratingPdf] = useState(new Set());
  const [pdfStatuses, setPdfStatuses] = useState(new Map());

  // Function to generate PDF report
  const handleGeneratePdf = useCallback(async (event, scanId) => {
    // Prevent default behavior and stop event propagation
    event?.preventDefault();
    event?.stopPropagation();
    
    // Check if PDF already exists
    try {
      const checkResponse = await authService.authenticatedCall(`${pentestServer}/results/files/${scanId}`);
      if (checkResponse.ok) {
        const ct = checkResponse.headers.get('content-type') || '';
        if (!ct.includes('application/json')) {
          // Not JSON, log a snippet for debugging and proceed
          const text = await checkResponse.text();
          console.log('Non-JSON response while checking files. First 120 chars:', text.slice(0, 120));
        } else {
          const data = await checkResponse.json();
          const pdfExists = data.files && data.files.some(file => file.name.includes('pentest-report') && file.name.endsWith('.pdf'));

          if (pdfExists) {
            const regenerate = showConfirm 
              ? await showConfirm({
                  title: 'PDF Report Exists',
                  message: 'A PDF report already exists for this scan. Do you want to regenerate it?',
                  confirmText: 'Regenerate',
                  confirmVariant: 'warning'
                })
              : window.confirm('A PDF report already exists for this scan. Do you want to regenerate it?');
            if (!regenerate) {
              return;
            }
          }
        }
      }
    } catch (error) {
      console.log('Could not check for existing PDF, proceeding with generation');
    }

    // Start PDF generation
    setGeneratingPdf(prev => new Set([...prev, scanId]));
    setPdfStatuses(prev => new Map([...prev, [scanId, 'Generating AI-powered PDF report...']]));

    try {
      const response = await authService.authenticatedCall(`${pentestServer}/report/pdf/${scanId}`);
      if (!response.ok) {
        let message = `HTTP error! status: ${response.status}`;
        try {
          const ct = response.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const errorData = await response.json();
            message = errorData.message || message;
          } else {
            const text = await response.text();
            message = `Unexpected response (expected JSON). First 200 chars: ${text.slice(0, 200)}`;
          }
        } catch (_) {}
        throw new Error(message);
      }

      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Unexpected response (expected JSON). First 120 chars: ${text.slice(0, 120)}`);
      }

      const result = await response.json();
      if (result.success) {
        setPdfStatuses(prev => new Map([...prev, [scanId, 'PDF generated successfully! Check the file browser below.']]))
        
        // Refresh the table row to show the new PDF file
        if (refreshTableData) {
          await refreshTableData();
        }
        
        // Also refresh result files if this is the current scan
        if (fetchResultFiles && (currentScanId === scanId || !isPentestLoading)) {
          await fetchResultFiles(scanId);
        }
        
        // Clear status after 5 seconds
        setTimeout(() => {
          setPdfStatuses(prev => {
            const newMap = new Map(prev);
            newMap.delete(scanId);
            return newMap;
          });
        }, 5000);
      } else {
        throw new Error(result.message || 'PDF generation failed');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfStatuses(prev => new Map([...prev, [scanId, `Error: ${error.message}`]]));
      
      // Clear error status after 8 seconds
      setTimeout(() => {
        setPdfStatuses(prev => {
          const newMap = new Map(prev);
          newMap.delete(scanId);
          return newMap;
        });
      }, 8000);
    } finally {
      setGeneratingPdf(prev => {
        const newSet = new Set(prev);
        newSet.delete(scanId);
        return newSet;
      });
    }
  }, [pentestServer, refreshTableData, fetchResultFiles, currentScanId, isPentestLoading, showConfirm]);

  return {
    generatingPdf,
    pdfStatuses,
    handleGeneratePdf
  };
};